const API = '/api/insights';
const OPENAI_URL = 'https://api.openai.com/v1/chat/completions';
const PHOTO_CACHE_PREFIX = 'glowlytix_ai_photo_';

function getToken() {
  return localStorage.getItem('glowlytix_token') || '';
}

function authHeaders() {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ─── Demo stub (shown when no token + no direct OpenAI key) ─────────────────

function generateDemoReport(entries) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  const recent = entries.filter(e => new Date(e.date) >= cutoff).slice(0, 40);
  const count = recent.length || entries.length;

  const scores = recent.map(e => e.glowScore || 0);
  const avg = scores.length
    ? parseFloat((scores.reduce((s, v) => s + v, 0) / scores.length).toFixed(1))
    : 7.2;

  let trend = 'stable';
  if (scores.length >= 4) {
    const half = Math.floor(scores.length / 2);
    const first = scores.slice(0, half).reduce((s, v) => s + v, 0) / half;
    const last  = scores.slice(-half).reduce((s, v) => s + v, 0) / half;
    if (last - first >= 0.5) trend = 'improving';
    else if (first - last >= 0.5) trend = 'declining';
  }

  return {
    id:         crypto.randomUUID(),
    generatedAt: new Date().toISOString(),
    entryCount: count,
    report: {
      summary: `Over the past 30 days your average glow score was ${avg}/10 and your skin trend is ${trend}. Your most consistent habit — SPF every morning — is making a measurable difference in overall tone and radiance.`,
      topInsight: 'Days with 64 oz or more of water consistently scored 1–2 points higher. Prioritising hydration is your single highest-leverage habit right now.',
      avgScore: avg,
      trend,
      boosters: ['High water intake (64+ oz)', 'Vitamin C serum in AM routine', 'Retinol 3× per week'],
      drainers: ['Late-night makeup removal skips', 'Low sleep on high-stress days'],
      recommendations: [
        'Set a water reminder for 10 am and 3 pm — reaching 64 oz before dinner is the fastest win in your data.',
        'Always double-cleanse after makeup days; entries without double cleansing average 1.4 points lower.',
        'Add niacinamide to your AM routine to target the uneven tone logged on 40% of your entries.',
      ],
      hormoneNote: 'Glow scores dipped by an average of 1.8 points on noted period days — consider scaling back actives and adding a barrier-repair cream those days.',
      makeupNote: 'Makeup days paired with double cleansing showed no negative impact on glow; single-cleanse makeup days averaged 1.4 points lower.',
    },
  };
}

async function handleResponse(res) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `Request failed (${res.status})`);
  }
  return res.json();
}

// ─── Direct OpenAI fallback (used when backend is unavailable) ──────────────

function getDirectKey() {
  return import.meta.env.VITE_OPENAI_API_KEY || '';
}

async function callOpenAIDirect(messages, model = 'gpt-4o') {
  const apiKey = getDirectKey();
  if (!apiKey) throw new Error('AI service unavailable. Please try again later or contact support.');

  const res = await fetch(OPENAI_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({ model, messages, temperature: 0.6, max_tokens: 900, response_format: { type: 'json_object' } }),
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error?.message || `AI error (${res.status})`);
  }

  const data = await res.json();
  return JSON.parse(data.choices[0]?.message?.content);
}

function buildEntryDigest(entries) {
  return [...entries]
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(e => {
      const parts = [`Date: ${e.date}`, `Glow score: ${e.glowScore}/10`];
      if (e.tags?.length)           parts.push(`Skin tags: ${e.tags.join(', ')}`);
      if (e.water)                  parts.push(`Water: ${e.water}oz`);
      if (e.routines?.AM?.length)   parts.push(`AM routine: ${e.routines.AM.join(', ')}`);
      if (e.routines?.PM?.length)   parts.push(`PM routine: ${e.routines.PM.join(', ')}`);
      if (e.treatments?.length)     parts.push(`Treatments: ${e.treatments.join(', ')}`);
      if (e.supplements?.length)    parts.push(`Supplements: ${e.supplements.join(', ')}`);
      if (e.makeup?.wore) {
        const prods = e.makeup.products?.join(', ') || 'yes';
        parts.push(`Makeup: ${prods}${e.makeup.doubleCleansed ? ' (double cleansed)' : ' (not double cleansed)'}`);
      } else {
        parts.push('Makeup: none');
      }
      if (e.cycle?.periodDay) {
        const flow     = e.cycle.flow ? ` (${e.cycle.flow} flow)` : '';
        const symptoms = e.cycle.symptoms?.length ? `, symptoms: ${e.cycle.symptoms.join(', ')}` : '';
        parts.push(`Cycle: period day${flow}${symptoms}`);
      }
      if (e.notes) parts.push(`Notes: "${e.notes}"`);
      return parts.join(' | ');
    })
    .join('\n');
}

async function generateReportDirect(entries) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 30);
  const recent = entries
    .filter(e => new Date(e.date) >= cutoff)
    .slice(0, 40);

  if (recent.length < 3) {
    throw new Error('Log at least 3 entries in the last 30 days for AI insights.');
  }

  const digest   = buildEntryDigest(recent);
  const avgScore = (recent.reduce((s, e) => s + (e.glowScore || 0), 0) / recent.length).toFixed(1);
  const periodDays = recent.filter(e => e.cycle?.periodDay).length;

  const messages = [
    {
      role: 'system',
      content: `You are a knowledgeable, warm skincare coach analyzing a user's daily skin tracking journal.
You have expertise in skincare science, hormonal skin patterns, and lifestyle's effect on skin health.
Always be encouraging, specific, and data-driven. Every recommendation must be grounded in the actual data provided.
Respond ONLY with valid JSON matching the exact schema requested.`,
    },
    {
      role: 'user',
      content: `Analyze my skin journal for the past 30 days and give me personalized insights.

SUMMARY STATS:
- Entries analyzed: ${recent.length}
- Average glow score: ${avgScore}/10
- Period days tracked: ${periodDays}

DAILY ENTRIES (oldest first):
${digest}

Return a JSON object with EXACTLY this structure:
{
  "summary": "2-3 sentence narrative overview of the skin journey this period",
  "topInsight": "The single most actionable finding from the data (1-2 sentences)",
  "glowTrend": "improving" | "declining" | "stable",
  "weeklyScore": <number 1-10>,
  "boosters": ["factor correlating with higher glow scores", "another booster"],
  "drainers": ["factor correlating with lower glow scores", "another drainer"],
  "recommendations": [
    "Specific actionable recommendation #1",
    "Specific actionable recommendation #2",
    "Specific actionable recommendation #3"
  ],
  "hormoneNote": "One sentence on cycle-skin relationship, or null",
  "makeupNote": "One sentence on makeup correlation, or null"
}`,
    },
  ];

  const report = await callOpenAIDirect(messages);
  return {
    id:          crypto.randomUUID(),
    generatedAt: new Date().toISOString(),
    entryCount:  recent.length,
    report,
  };
}

// ─── Public API ─────────────────────────────────────────────────────────────

/** Generate a new AI insight report via backend API, with fallbacks for demo / offline. */
export async function generateReport(entries) {
  // Demo users have no JWT — skip the API and return a realistic stub instantly
  if (!getToken()) {
    await new Promise(r => setTimeout(r, 1400)); // simulate thinking
    return generateDemoReport(entries);
  }

  try {
    const res = await fetch(`${API}/generate`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ entries }),
    });
    if (res.ok) return res.json();
    // 400 means a real validation error from server — surface it directly
    if (res.status === 400) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Not enough entries.');
    }
    // Any other server error: fall back to direct OpenAI call if key available
    if (getDirectKey()) return generateReportDirect(entries);
    throw new Error('AI service temporarily unavailable. Please try again later.');
  } catch (e) {
    // Network error — try direct if we have a client-side key, else re-throw
    if (getDirectKey()) return generateReportDirect(entries);
    throw e;
  }
}

/** Fetch all saved insight reports. Returns empty array when backend unavailable. */
export async function getInsights() {
  try {
    const res = await fetch(`${API}`, { headers: authHeaders() });
    if (res.ok) return res.json();
  } catch { /* ignore */ }
  return [];
}

/** Soft-delete an insight report by ID. Best-effort. */
export async function deleteInsight(id) {
  try {
    const res = await fetch(`${API}/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (res.ok) return res.json();
  } catch { /* ignore */ }
  return { ok: true };
}

/** Analyze a single photo via backend, or directly if backend is unavailable. */
export async function analyzePhoto(imageBase64OrUrl, entryContext = {}) {
  const entryId = entryContext.id;
  if (entryId) {
    try {
      const cached = localStorage.getItem(PHOTO_CACHE_PREFIX + entryId);
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
  }

  let result;
  try {
    const res = await fetch(`${API}/analyze-photo`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ photoUrl: imageBase64OrUrl, context: entryContext }),
    });
    if (res.ok) {
      result = await res.json();
    }
  } catch { /* fall through */ }

  if (!result) {
    const contextNote = entryContext.glowScore ? `The user rated their glow ${entryContext.glowScore}/10 on this day.` : '';
    const tagsNote    = entryContext.tags?.length ? `They noted: ${entryContext.tags.join(', ')}.` : '';

    const messages = [
      {
        role: 'system',
        content: `You are a professional skincare consultant analyzing skin photos for a tracking app.
Be objective, encouraging, and clinically observant. Focus on visible skin characteristics only.
Never make medical diagnoses. Respond ONLY with valid JSON.`,
      },
      {
        role: 'user',
        content: [
          {
            type: 'text',
            text: `Analyze the skin visible in this photo. ${contextNote} ${tagsNote}

Return EXACTLY this JSON structure:
{
  "overall": "one sentence describing the overall appearance",
  "texture": "smooth" | "slightly uneven" | "uneven",
  "hydration": "dewy" | "normal" | "dry-looking",
  "tone": "even" | "slightly uneven" | "uneven",
  "glowRating": <number 1-10>,
  "positives": ["specific positive observation", "another positive"],
  "watchPoints": ["specific thing to monitor"],
  "tip": "One specific skincare tip based on what you see"
}`,
          },
          { type: 'image_url', image_url: { url: imageBase64OrUrl, detail: 'low' } },
        ],
      },
    ];

    const analysis = await callOpenAIDirect(messages, 'gpt-4o');
    result = { analysis, analyzedAt: new Date().toISOString() };
  }

  if (entryId) {
    try {
      localStorage.setItem(PHOTO_CACHE_PREFIX + entryId, JSON.stringify(result));
    } catch { /* ignore */ }
  }
  return result;
}

/** Clear locally cached photo analyses. */
export function clearPhotoCache() {
  Object.keys(localStorage)
    .filter(k => k.startsWith(PHOTO_CACHE_PREFIX))
    .forEach(k => localStorage.removeItem(k));
}
